import { useEffect, useState } from "react";
import clsx from "clsx";

import { capitalizeString } from "@excalidraw/common";

import { trackEvent } from "../analytics";

import { t } from "../i18n";

import { isHandToolActive } from "../appState";

import { HandButton } from "./HandButton";
import { ToolButton } from "./ToolButton";
import { ToolPopover } from "./ToolPopover";

import {
  SelectionIcon,
  FreedrawIcon,
  EraserIcon,
  RectangleIcon,
  GeometricShapesIcon,
  ArrowIcon,
  TriangleIcon,
  EllipseIcon,
  LineIcon,
  TextIcon,
  ImageIcon,
  frameToolIcon,
  laserPointerToolIcon,
  LassoIcon,
  papelIcon,
} from "./icons";

import "./ToolIcon.scss";
import "./MobileToolBar.scss";

import type { AppClassProperties, ToolType, UIAppState } from "../types";

const SHAPE_TOOLS = [
  {
    type: "rectangle",
    icon: RectangleIcon,
    title: capitalizeString(t("toolBar.rectangle")),
  },
  {
    type: "triangle",
    icon: TriangleIcon,
    title: capitalizeString(t("toolBar.triangle")),
  },
  {
    type: "ellipse",
    icon: EllipseIcon,
    title: capitalizeString(t("toolBar.ellipse")),
  },
] as const;

const SELECTION_TOOLS = [
  {
    type: "selection",
    icon: SelectionIcon,
    title: capitalizeString(t("toolBar.selection")),
  },
  {
    type: "lasso",
    icon: LassoIcon,
    title: capitalizeString(t("toolBar.lasso")),
  },
] as const;

const LINEAR_ELEMENT_TOOLS = [
  {
    type: "arrow",
    icon: ArrowIcon,
    title: capitalizeString(t("toolBar.arrow")),
  },
  {
    type: "line",
    icon: LineIcon,
    title: capitalizeString(t("toolBar.line")),
  },
] as const;

const CANVAS_TOOLS = [
  {
    type: "custom",
    customType: "papel",
    icon: papelIcon,
    title: "Papel",
  },
  {
    type: "frame",
    customType: null,
    icon: frameToolIcon,
    title: capitalizeString(t("toolBar.frame")),
  },
] as const;

const FREEHAND_TOOLS = [
  {
    type: "freedraw",
    icon: FreedrawIcon,
    title: capitalizeString(t("toolBar.freedraw")),
  },
  {
    type: "laser",
    icon: laserPointerToolIcon,
    title: capitalizeString(t("toolBar.laser")),
  },
] as const;

type MobileToolBarProps = {
  app: AppClassProperties;
  onHandToolToggle: () => void;
  setAppState: React.Component<any, UIAppState>["setState"];
};

export const MobileToolBar = ({
  app,
  onHandToolToggle,
  setAppState,
}: MobileToolBarProps) => {
  const activeTool = app.state.activeTool;
  const [lastActiveGenericShape, setLastActiveGenericShape] = useState<
    "rectangle" | "triangle" | "ellipse"
  >("rectangle");
  const [lastActiveLinearElement, setLastActiveLinearElement] = useState<
    "arrow" | "line"
  >("arrow");

  // keep lastActiveGenericShape in sync with active tool if user switches via other UI
  useEffect(() => {
    if (
      activeTool.type === "rectangle" ||
      activeTool.type === "triangle" ||
      activeTool.type === "ellipse"
    ) {
      setLastActiveGenericShape(activeTool.type);
    }
  }, [activeTool.type]);

  // keep lastActiveLinearElement in sync with active tool if user switches via other UI
  useEffect(() => {
    if (activeTool.type === "arrow" || activeTool.type === "line") {
      setLastActiveLinearElement(activeTool.type);
    }
  }, [activeTool.type]);

  const handleToolChange = (toolType: string, pointerType?: string) => {
    if (app.state.activeTool.type !== toolType) {
      trackEvent("toolbar", toolType, "ui");
    }

    if (toolType === "selection") {
      if (app.state.activeTool.type === "selection") {
        // Toggle selection tool behavior if needed
      } else {
        app.setActiveTool({ type: "selection" });
      }
    } else {
      app.setActiveTool({ type: toolType as ToolType });
    }
  };

  const showTextToolOutside = true;
  const showImageToolOutside = true;

  return (
    <div className="mobile-toolbar">
      {/* Hand Tool */}
      <HandButton
        checked={isHandToolActive(app.state)}
        onChange={onHandToolToggle}
        title={t("toolBar.hand")}
        isMobile
      />

      {/* Selection Tool */}
      <ToolPopover
        app={app}
        options={SELECTION_TOOLS}
        activeTool={activeTool}
        defaultOption={app.state.preferredSelectionTool.type}
        namePrefix="selectionType"
        title={capitalizeString(t("toolBar.selection"))}
        data-testid="toolbar-selection"
        onToolChange={(type: string) => {
          if (type === "selection" || type === "lasso") {
            app.setActiveTool({ type });
            setAppState({
              preferredSelectionTool: { type, initialized: true },
            });
          }
        }}
        displayedOption={
          SELECTION_TOOLS.find(
            (tool) => tool.type === app.state.preferredSelectionTool.type,
          ) || SELECTION_TOOLS[0]
        }
      />

      {/* Free Draw */}
      <ToolPopover
        app={app}
        options={FREEHAND_TOOLS}
        activeTool={activeTool}
        defaultOption={app.state.preferredFreedrawTool?.type || "freedraw"}
        namePrefix="freedrawToolType"
        title={
          activeTool.type === "freedraw" || activeTool.type === "laser"
            ? activeTool.type === "freedraw"
              ? capitalizeString(t("toolBar.freedraw"))
              : capitalizeString(t("toolBar.laser"))
            : capitalizeString(t("toolBar.freedraw"))
        }
        data-testid="toolbar-freedraw-tools"
        fillable={false}
        selectOnOpen={false}
        side="top"
        onToolChange={(type: string) => {
          if (type === "freedraw" || type === "laser") {
            app.setActiveTool({ type });
            setAppState({
              preferredFreedrawTool: {
                type: type as "freedraw" | "laser",
                initialized: true,
              },
            });
          }
        }}
        displayedOption={{
          ...(FREEHAND_TOOLS.find(
            (tool) => tool.type === app.state.preferredFreedrawTool?.type,
          ) || FREEHAND_TOOLS[0]),
          icon:
            activeTool.type === "freedraw" || activeTool.type === "laser"
              ? (
                  FREEHAND_TOOLS.find(
                    (tool) =>
                      tool.type === app.state.preferredFreedrawTool?.type,
                  ) || FREEHAND_TOOLS[0]
                ).icon
              : FreedrawIcon,
        }}
      />

      {/* Eraser */}
      <ToolButton
        className={clsx({
          active: activeTool.type === "eraser",
        })}
        type="radio"
        icon={EraserIcon}
        checked={activeTool.type === "eraser"}
        name="editor-current-shape"
        title={`${capitalizeString(t("toolBar.eraser"))}`}
        aria-label={capitalizeString(t("toolBar.eraser"))}
        data-testid="toolbar-eraser"
        onChange={() => handleToolChange("eraser")}
      />

      {/* Geometric Shapes */}
      <ToolPopover
        app={app}
        options={SHAPE_TOOLS}
        activeTool={activeTool}
        defaultOption={lastActiveGenericShape}
        namePrefix="geometricShapeType"
        title={
          activeTool.type === "rectangle" ||
          activeTool.type === "triangle" ||
          activeTool.type === "ellipse"
            ? capitalizeString(t(`toolBar.${activeTool.type}`))
            : capitalizeString(t("headings.shapes"))
        }
        data-testid="toolbar-geometric-shapes"
        fillable={true}
        selectOnOpen={false}
        side="top"
        onToolChange={(type: string) => {
          if (
            type === "rectangle" ||
            type === "triangle" ||
            type === "ellipse"
          ) {
            setLastActiveGenericShape(
              type as "rectangle" | "triangle" | "ellipse",
            );
            app.setActiveTool({ type });
            setAppState({
              preferredGeometricShape: {
                type: type as "rectangle" | "triangle" | "ellipse",
                initialized: true,
              },
            });
          }
        }}
        displayedOption={{
          ...(SHAPE_TOOLS.find(
            (tool) => tool.type === lastActiveGenericShape,
          ) || SHAPE_TOOLS[0]),
          icon:
            activeTool.type === "rectangle" ||
            activeTool.type === "triangle" ||
            activeTool.type === "ellipse"
              ? SHAPE_TOOLS.find((tool) => tool.type === activeTool.type)
                  ?.icon || GeometricShapesIcon
              : GeometricShapesIcon,
        }}
      />

      {/* Papel e Frame (Canvas Tools) */}
      <ToolPopover
        app={app}
        options={CANVAS_TOOLS}
        activeTool={activeTool}
        defaultOption={app.state.preferredCanvasTool?.type || "papel"}
        namePrefix="canvasToolType"
        title={
          activeTool.type === "frame" ||
          (activeTool.type === "custom" && activeTool.customType === "papel")
            ? activeTool.type === "frame"
              ? capitalizeString(t("toolBar.frame"))
              : "Papel"
            : "Papel e Quadro"
        }
        data-testid="toolbar-canvas-tools"
        fillable={false}
        selectOnOpen={false}
        side="top"
        onToolChange={(type: string) => {
          if (type === "frame" || type === "papel") {
            if (type === "frame") {
              app.setActiveTool({ type: "frame" });
            } else {
              app.setActiveTool({ type: "custom", customType: "papel" });
            }
            setAppState({
              preferredCanvasTool: {
                type: type as "frame" | "papel",
                initialized: true,
              },
            });
          }
        }}
        displayedOption={
          CANVAS_TOOLS.find(
            (tool) =>
              (tool.customType || tool.type) ===
              app.state.preferredCanvasTool?.type,
          ) || CANVAS_TOOLS[0]
        }
      />

      {/* Arrow/Line */}
      <ToolPopover
        app={app}
        options={LINEAR_ELEMENT_TOOLS}
        activeTool={activeTool}
        defaultOption={lastActiveLinearElement}
        namePrefix="linearElementType"
        title={capitalizeString(
          t(
            lastActiveLinearElement === "arrow"
              ? "toolBar.arrow"
              : "toolBar.line",
          ),
        )}
        data-testid="toolbar-arrow"
        fillable={true}
        onToolChange={(type: string) => {
          if (type === "arrow" || type === "line") {
            setLastActiveLinearElement(type);
            app.setActiveTool({ type });
          }
        }}
        displayedOption={
          LINEAR_ELEMENT_TOOLS.find(
            (tool) => tool.type === lastActiveLinearElement,
          ) || LINEAR_ELEMENT_TOOLS[0]
        }
      />

      {/* Text Tool */}
      {showTextToolOutside && (
        <ToolButton
          className={clsx({
            active: activeTool.type === "text",
          })}
          type="radio"
          icon={TextIcon}
          checked={activeTool.type === "text"}
          name="editor-current-shape"
          title={`${capitalizeString(t("toolBar.text"))}`}
          aria-label={capitalizeString(t("toolBar.text"))}
          data-testid="toolbar-text"
          onChange={() => handleToolChange("text")}
        />
      )}

      {/* Image */}
      {showImageToolOutside && (
        <ToolButton
          className={clsx({
            active: activeTool.type === "image",
          })}
          type="radio"
          icon={ImageIcon}
          checked={activeTool.type === "image"}
          name="editor-current-shape"
          title={`${capitalizeString(t("toolBar.image"))}`}
          aria-label={capitalizeString(t("toolBar.image"))}
          data-testid="toolbar-image"
          onChange={() => handleToolChange("image")}
        />
      )}

      {/* Extra tools removed per user request */}
    </div>
  );
};
